/**
 * Lightweight connector display data: display names and icon components only.
 *
 * This module is the single source of truth for connector display metadata
 * (name + icon). It deliberately avoids importing the full form-builder
 * connector configs, feature-gate logic, URL constants, and heavy UI
 * components so that consumers who only need display information (e.g. the
 * `/connector-display-utils` entry point) do not pull in the entire
 * form-builder dependency graph.
 *
 * The full `configsMap` in `displayed-third-party-products.tsx` references
 * this data via each connector config's `connectorDisplayName` and `icon`
 * fields, which remain the canonical values defined in each config file.
 * This module duplicates only the display name message descriptors so it can
 * stand alone without importing those config files.
 */
import React from 'react';

import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import {
	adobeSignIcon,
	ahaIcon,
	airtableIcon,
	asanaIcon,
	azureDevopsIcon,
	boxIcon,
	canvaIcon,
	confluenceDcIcon,
	databricksIcon,
	datadogIcon,
	docusignIcon,
	dropboxIcon,
	dynatraceIcon,
	githubIcon,
	gitlabIcon,
	globeIcon,
	gmailIcon,
	googleCalendarIcon,
	googleDriveIcon,
	hrisIcon,
	hubspotIcon,
	jiraDcIcon,
	lucidIcon,
	microsoftOutlookCalendarIcon,
	microsoftOutlookMailIcon,
	microsoftSharepointIcon,
	microsoftTeamsIcon,
	miroIcon,
	mondayIcon,
	newrelicIcon,
	notionIcon,
	rocketImage,
	salesforceIcon,
	serviceNowIcon,
	slackIcon,
	smartsheetIcon,
	spinnakerIcon,
	workdayIcon,
	zendeskIcon,
} from '../images';

import { ConnectorLogo } from './styles';
import type { ThirdPartyConnectorType } from './third-party-connector-form-builder/types';

// ---------------------------------------------------------------------------
// Display name messages
// ---------------------------------------------------------------------------
// These mirror the `connectorDisplayName` message descriptors defined in each
// connector config file. The message IDs MUST stay in sync with the originals.

const displayMessages = defineMessages({
	adobeSign: {
		id: 'adobe-sign-connector.connector-display-name',
		defaultMessage: 'Adobe Sign',
		description: 'Display name for the Adobe Sign connector.',
	},
	aha: {
		id: 'aha-connector.connector-display-name',
		defaultMessage: 'Aha!',
		description: 'Display name for the Aha! third-party search connector shown in the admin UI.',
	},
	airtable: {
		id: 'airtable-connector.connector-display-name',
		defaultMessage: 'Airtable',
		description: 'Display name for the Airtable connector.',
	},
	asana: {
		id: 'asana-connector.connector-display-name',
		defaultMessage: 'Asana',
		description: 'Display name for the Asana connector.',
	},
	azureDevops: {
		id: 'azure-devops-connector.connector-display-name',
		defaultMessage: 'Azure DevOps',
		description: 'Display name for the Azure DevOps connector.',
	},
	box: {
		id: 'box-connector.connector-display-name',
		defaultMessage: 'Box',
		description: 'Display name for the Box third-party search connector shown in the admin UI.',
	},
	canva: {
		id: 'canva-connector.connector-display-name',
		defaultMessage: 'Canva',
		description: 'Display name for the Canva connector.',
	},
	confluenceDC: {
		id: 'confluence-dc-connector.connector-display-name',
		defaultMessage: 'Confluence Data Center',
		description: 'Display name for the Confluence Data Center connector.',
	},
	databricks: {
		id: 'databricks-connector.connector-display-name',
		defaultMessage: 'Databricks',
		description: 'Display name for the Databricks connector.',
	},
	datadog: {
		id: 'datadog-connector.connector-display-name',
		defaultMessage: 'Datadog',
		description: 'Display name for the Datadog connector.',
	},
	demoConnector: {
		id: 'demo-connector.connector-display-name.nonfinal',
		defaultMessage: 'Demo Connector',
		description: 'Display name shown for the Demo connector in the connector catalog and settings.',
	},
	docusign: {
		id: 'docusign-connector.connector-display-name',
		defaultMessage: 'Docusign',
		description: 'Display name for the Docusign connector.',
	},
	dropbox: {
		id: 'dropbox-connector.connector-display-name',
		defaultMessage: 'Dropbox',
		description:
			'Display name shown for the Dropbox connector in the connector catalog and settings.',
	},
	dynatrace: {
		id: 'dynatrace-connector.connector-display-name.nonfinal',
		defaultMessage: 'Dynatrace',
		description: 'Display name for the Dynatrace connector.',
	},
	exampleConnector: {
		id: 'example-connector-config.connector-display-name.nonfinal',
		defaultMessage: 'Example Connector',
		description: 'Display name for the example connector (local testing).',
	},
	exampleConnectorOAuth: {
		id: 'example-connector-config.oauth-connector-display-name.nonfinal',
		defaultMessage: 'Example Connector OAuth',
		description: 'Display name for the example OAuth connector (local testing).',
	},
	github: {
		id: 'github-connector.connector-display-name',
		defaultMessage: 'GitHub',
		description: 'Display name for the GitHub connector.',
	},
	gitlab: {
		id: 'gitlab-connector.connector-display-name',
		defaultMessage: 'GitLab',
		description: 'Display name for the GitLab connector.',
	},
	gmail: {
		id: 'gmail-connector',
		defaultMessage: 'Gmail',
		description:
			'Display name shown for the Gmail connector in the connector catalog and settings.',
	},
	googleCalendar: {
		id: 'google-calendar-connector',
		defaultMessage: 'Google Calendar',
		description: 'Display name for the Google Calendar connector.',
	},
	googleDrive: {
		id: 'google-drive-connector.display-name',
		defaultMessage: 'Google Drive',
		description: 'Display name for the Google Drive connector.',
	},
	hris: {
		id: 'hris-s3-connector.connector-display-name',
		defaultMessage: 'HRIS S3',
		description: 'Display name for the HRIS S3 connector.',
	},
	hubspot: {
		id: 'hubspot-connector.connector-display-name',
		defaultMessage: 'HubSpot',
		description: 'Display name for the HubSpot connector.',
	},
	jiraDC: {
		id: 'jira-dc-connector.connector-display-name',
		defaultMessage: 'Jira Data Center',
		description: 'Display name for the Jira Data Center connector.',
	},
	lucid: {
		id: 'lucid-connector.connector-display-name',
		defaultMessage: 'Lucid',
		description: 'Display name for the Lucid connector.',
	},
	microsoftTeams: {
		id: 'teams-connector.connector-display-name',
		defaultMessage: 'Microsoft Teams',
		description: 'Display name for the Microsoft Teams connector.',
	},
	miro: {
		id: 'miro-connector.connector-display-name',
		defaultMessage: 'Miro',
		description: 'Display name for the Miro connector.',
	},
	monday: {
		id: 'monday-connector.connector-display-name',
		defaultMessage: 'Monday',
		description: 'Display name for the Monday connector.',
	},
	newrelic: {
		id: 'newrelic-connector.connector-display-name',
		defaultMessage: 'NewRelic',
		description: 'Display name for the NewRelic connector.',
	},
	notion: {
		id: 'notion-connector.connector-display-name',
		defaultMessage: 'Notion',
		description: 'Display name for the Notion connector.',
	},
	outlookCalendar: {
		id: 'outlook-calendar-connector.connector-display-name',
		defaultMessage: 'Microsoft Outlook Calendar',
		description: 'Display name for the Outlook Calendar connector.',
	},
	outlookMail: {
		id: 'outlook-mail-connector.connector-display-name',
		defaultMessage: 'Microsoft Outlook Mail',
		description: 'Display name for the Outlook Mail connector.',
	},
	platformSharepoint: {
		id: 'sharepoint-connector.platform-connector-display-name',
		defaultMessage: 'Microsoft SharePoint and OneDrive',
		description: 'Display name for the SharePoint platform connector.',
	},
	salesforce: {
		id: 'salesforce-connector.connector-display-name',
		defaultMessage: 'Salesforce',
		description: 'Display name for the Salesforce connector.',
	},
	servicenow: {
		id: 'servicenow-connector.connector-display-name',
		defaultMessage: 'ServiceNow',
		description: 'Display name for the ServiceNow connector.',
	},
	slack: {
		id: 'slack-connector.connector-display-name',
		defaultMessage: 'Slack',
		description: 'Display name for the Slack connector.',
	},
	smartsheet: {
		id: 'smartsheet-connector.connector-display-name',
		defaultMessage: 'Smartsheet',
		description: 'Display name for the Smartsheet connector.',
	},
	spinnaker: {
		id: 'spinnaker-connector.connector-display-name',
		defaultMessage: 'Spinnaker',
		description: 'Display name for the Spinnaker connector.',
	},
	webCrawler: {
		id: 'web-crawler-connector.connector-display-name',
		defaultMessage: 'Custom website',
		description: 'Display name for the Web Crawler connector.',
	},
	workday: {
		id: 'workday-connector.connector-display-name',
		defaultMessage: 'Workday',
		description: 'Display name for the Workday connector.',
	},
	workdayV2: {
		id: 'workday-v2-connector.connector-display-name',
		defaultMessage: 'Workday',
		description: 'Display name for the Workday V2 connector.',
	},
	zendesk: {
		id: 'zendesk-connector.connector-display-name',
		defaultMessage: 'Zendesk',
		description: 'Display name for the Zendesk connector.',
	},
});

// ---------------------------------------------------------------------------
// Display data type and map
// ---------------------------------------------------------------------------

export interface ConnectorDisplayEntry {
	displayName: MessageDescriptor;
	icon: React.FC<{ size?: any }>;
}

/**
 * Lightweight map of connector type keys to display metadata.
 *
 * Contains only what is needed to show a connector's name and logo in UI
 * surfaces. No form-builder configs, no feature-gate side effects, no URL
 * constants.
 */
export const connectorDisplayDataMap: Record<ThirdPartyConnectorType, ConnectorDisplayEntry> = {
	'test-connector-config-local': {
		displayName: displayMessages.exampleConnector,
		icon: () => <ConnectorLogo logo="" />,
	},
	'test-connector-config-local-oauth': {
		displayName: displayMessages.exampleConnectorOAuth,
		icon: () => <ConnectorLogo logo="" />,
	},
	'demo-connector': {
		displayName: displayMessages.demoConnector,
		icon: () => <ConnectorLogo logo={rocketImage} />,
	},
	'github-connector': {
		displayName: displayMessages.github,
		icon: () => <ConnectorLogo logo={githubIcon} />,
	},
	'microsoft-teams-connector': {
		displayName: displayMessages.microsoftTeams,
		icon: () => <ConnectorLogo logo={microsoftTeamsIcon} />,
	},
	'gmail-connector': {
		displayName: displayMessages.gmail,
		icon: () => <ConnectorLogo logo={gmailIcon} />,
	},
	'google-calendar-connector': {
		displayName: displayMessages.googleCalendar,
		icon: () => <ConnectorLogo logo={googleCalendarIcon} />,
	},
	'dropbox-connector': {
		displayName: displayMessages.dropbox,
		icon: () => <ConnectorLogo logo={dropboxIcon} />,
	},
	'salesforce-connector': {
		displayName: displayMessages.salesforce,
		icon: () => <ConnectorLogo logo={salesforceIcon} />,
	},
	'outlook-mail-connector': {
		displayName: displayMessages.outlookMail,
		icon: () => <ConnectorLogo logo={microsoftOutlookMailIcon} />,
	},
	'airtable-connector': {
		displayName: displayMessages.airtable,
		icon: () => <ConnectorLogo logo={airtableIcon} />,
	},
	'asana-connector': {
		displayName: displayMessages.asana,
		icon: () => <ConnectorLogo logo={asanaIcon} />,
	},
	'smartsheet-connector': {
		displayName: displayMessages.smartsheet,
		icon: () => <ConnectorLogo logo={smartsheetIcon} />,
	},
	'outlook-calendar-connector': {
		displayName: displayMessages.outlookCalendar,
		icon: () => <ConnectorLogo logo={microsoftOutlookCalendarIcon} />,
	},
	'notion-connector': {
		displayName: displayMessages.notion,
		icon: () => <ConnectorLogo logo={notionIcon} />,
	},
	'zendesk-connector': {
		displayName: displayMessages.zendesk,
		icon: () => <ConnectorLogo logo={zendeskIcon} />,
	},
	'docusign-connector': {
		displayName: displayMessages.docusign,
		icon: () => <ConnectorLogo logo={docusignIcon} />,
	},
	'servicenow-connector': {
		displayName: displayMessages.servicenow,
		icon: () => <ConnectorLogo logo={serviceNowIcon} />,
	},
	'miro-connector': {
		displayName: displayMessages.miro,
		icon: () => <ConnectorLogo logo={miroIcon} />,
	},
	'gitlab-connector': {
		displayName: displayMessages.gitlab,
		icon: () => <ConnectorLogo logo={gitlabIcon} />,
	},
	'lucid-connector': {
		displayName: displayMessages.lucid,
		icon: () => <ConnectorLogo logo={lucidIcon} />,
	},
	'box-connector': {
		displayName: displayMessages.box,
		icon: () => <ConnectorLogo logo={boxIcon} />,
	},
	'canva-connector': {
		displayName: displayMessages.canva,
		icon: () => <ConnectorLogo logo={canvaIcon} />,
	},
	'azure-devops-connector': {
		displayName: displayMessages.azureDevops,
		icon: () => <ConnectorLogo logo={azureDevopsIcon} />,
	},
	'adobe-sign-connector': {
		displayName: displayMessages.adobeSign,
		icon: () => <ConnectorLogo logo={adobeSignIcon} />,
	},
	'confluence-dc-connector': {
		displayName: displayMessages.confluenceDC,
		icon: () => <ConnectorLogo logo={confluenceDcIcon} />,
	},
	'platform-sharepoint-connector': {
		displayName: displayMessages.platformSharepoint,
		icon: () => <ConnectorLogo logo={microsoftSharepointIcon} />,
	},
	'jira-dc-connector': {
		displayName: displayMessages.jiraDC,
		icon: () => <ConnectorLogo logo={jiraDcIcon} />,
	},
	'monday-connector': {
		displayName: displayMessages.monday,
		icon: () => <ConnectorLogo logo={mondayIcon} />,
	},
	'aha-connector': {
		displayName: displayMessages.aha,
		icon: () => <ConnectorLogo logo={ahaIcon} />,
	},
	'workday-connector': {
		displayName: displayMessages.workday,
		icon: () => <ConnectorLogo logo={workdayIcon} />,
	},
	'workday-v2-connector': {
		displayName: displayMessages.workdayV2,
		icon: () => <ConnectorLogo logo={workdayIcon} />,
	},
	'platform-google-calendar-connector': {
		displayName: displayMessages.googleCalendar,
		icon: () => <ConnectorLogo logo={googleCalendarIcon} />,
	},
	'platform-google-drive-connector': {
		displayName: displayMessages.googleDrive,
		icon: () => <ConnectorLogo logo={googleDriveIcon} />,
	},
	'spinnaker-connector': {
		displayName: displayMessages.spinnaker,
		icon: () => <ConnectorLogo logo={spinnakerIcon} />,
	},
	'slack-connector': {
		displayName: displayMessages.slack,
		icon: () => <ConnectorLogo logo={slackIcon} />,
	},
	'hubspot-connector': {
		displayName: displayMessages.hubspot,
		icon: () => <ConnectorLogo logo={hubspotIcon} />,
	},
	'datadog-connector': {
		displayName: displayMessages.datadog,
		icon: () => <ConnectorLogo logo={datadogIcon} />,
	},
	'newrelic-connector': {
		displayName: displayMessages.newrelic,
		icon: () => <ConnectorLogo logo={newrelicIcon} />,
	},
	'dynatrace-connector': {
		displayName: displayMessages.dynatrace,
		icon: () => <ConnectorLogo logo={dynatraceIcon} />,
	},
	'databricks-connector': {
		displayName: displayMessages.databricks,
		icon: () => <ConnectorLogo logo={databricksIcon} />,
	},
	'hris-s3-connector': {
		displayName: displayMessages.hris,
		icon: () => <ConnectorLogo logo={hrisIcon} />,
	},
	'webcrawler-connector': {
		displayName: displayMessages.webCrawler,
		icon: () => <ConnectorLogo logo={globeIcon} />,
	},
	'platform-webcrawler-connector': {
		displayName: displayMessages.webCrawler,
		icon: () => <ConnectorLogo logo={globeIcon} />,
	},
};
